import type { Meter } from '@tunasong/models'
import { meterBottom, meterTop } from '@tunasong/models'
import { concertKeyOf, keyName } from '@tunasong/music-lib'
import type { PitchFrame } from '@tunasong/plugin-lib'
import type { PerformancePart, Scale, Song } from '@tunasong/schemas'

/** Compound meters: the beat is a dotted quarter. */
const isCompound = (meter: Meter) => meterBottom(meter) === 8 && [6, 9, 12].includes(meterTop(meter))

/**
 * Felt beats per bar (D12): x/4 counts the top number; 6/8, 9/8 and 12/8 count dotted quarters (2, 3, 4);
 * other x/8 (5/8, 7/8) count the top number. Grouping such as 2+2+3 is follow-up F9.
 */
export const feltBeats = (meter: Meter) => (isCompound(meter) ? meterTop(meter) / 3 : meterTop(meter))

/** "count-in 1 bar · 3": bars, then clicks. */
export const countInLabel = (meter: Meter) => `count-in 1 bar · ${feltBeats(meter)}`

/**
 * The tempo as a performer counts it. `rhythm.tempo` is quarter-note BPM (the engine feeds it to the
 * transport as such), so a compound meter shows the dotted quarter: ♩. = tempo × 2/3.
 */
export const tempoLabel = (tempo: number, meter: Meter | undefined) =>
  meter && isCompound(meter) ? `♩.=${Math.round((tempo * 2) / 3)}` : `♩=${tempo}`

/** Parts that read pitch: each of them needs the key line. Drums add no pitch. */
const pitchParts: readonly PerformancePart[] = ['vocals', 'guitar', 'bass', 'keys', 'full']

const keyText = (key: Scale) => `Key ${keyName(key)}`

/**
 * The key and capo segment, one form per set (D15, Bowie):
 * - Full chart: the key as charted, then the capo badge ("Key G · Capo 3 · sounds in B♭"); on a chart
 *   written in concert pitch, the guitar's capo and shapes ("Key B♭ · guitar capo 3, G shapes");
 * - Guitar alone: the capo badge, in both frames ("Capo 3 · sounds in B♭");
 * - Guitar with another pitch part: the concert key first, then the guitar's capo, worded by the frame
 *   ("Key B♭ · capo 3, G shapes" in Shapes, "Key B♭ · guitar capo 3" in Concert);
 * - otherwise: the concert key ("Key B♭").
 * Without a capo, every form is the key alone; without a key, only the capo can be said.
 */
const keySegment = (parts: readonly PerformancePart[], song: Song, frame: PitchFrame) => {
  if (!parts.some(part => pitchParts.includes(part))) {
    return undefined
  }
  const capo = song.capo ?? 0
  const writtenAs = song.chordsWrittenAs ?? 'shapes'
  const written = song.scale ?? undefined
  const concert = concertKeyOf(written, capo, writtenAs)
  const shapes = writtenAs === 'shapes' ? written : concertKeyOf(written, -capo, 'shapes')
  if (capo === 0) {
    return written ? keyText(written) : undefined
  }
  const badge = concert ? `Capo ${capo} · sounds in ${keyName(concert)}` : `Capo ${capo}`
  if (parts.includes('full')) {
    if (writtenAs === 'concert') {
      /** Bowie Q3: a concert chart's key is the concert key; the capo and shapes are the guitar's */
      return written && shapes
        ? `${keyText(written)} · guitar capo ${capo}, ${keyName(shapes)} shapes`
        : `Guitar capo ${capo}`
    }
    return written ? `${keyText(written)} · ${badge}` : badge
  }
  const guitar = parts.includes('guitar')
  if (guitar && parts.filter(part => pitchParts.includes(part)).length === 1) {
    return badge
  }
  if (guitar) {
    const capoText = frame === 'concert' || !shapes ? `guitar capo ${capo}` : `capo ${capo}, ${keyName(shapes)} shapes`
    return concert ? `${keyText(concert)} · ${capoText}` : capoText
  }
  return concert ? keyText(concert) : undefined
}

/** Guitar only: a tuning that is not standard. */
const tuningSegment = (parts: readonly PerformancePart[], song: Song) => {
  const tuning = song.tuning?.trim()
  return parts.includes('guitar') && tuning && tuning.toLowerCase() !== 'standard' ? tuning : undefined
}

const meterText = (meter: Meter) => `${meterTop(meter)}/${meterBottom(meter)}`

/**
 * The header line for the performer's parts, merged by meaning in a fixed order (D15): key and capo, then
 * tuning, then tempo, then count-in, then the person's transpose, each at most once. The keys follow the
 * transpose; the capo does not (Bowie Q5).
 * - key and capo: any part that reads pitch (see `keySegment`); `frame` (absent = Shapes) words it;
 * - tuning: Guitar, when not standard;
 * - tempo, meter and feel: Drums only (the metronome shows tempo to everyone else);
 * - count-in: Drums or Vocals.
 * "straight" shows unless the song swings (`rhythm.triplet`); a named feel arrives with `rhythm.feel`.
 */
/** "transposed +5", "transposed −2" (a real minus sign). */
const transposeText = (transpose: number) => `transposed ${transpose > 0 ? '+' : '\u2212'}${Math.abs(transpose)}`

export const partHeaderLine = ({
  parts,
  song,
  frame = 'shapes',
  transpose = 0,
}: {
  parts: readonly PerformancePart[]
  song: Song
  frame?: PitchFrame
  /** The person's transpose: the keys move with it, the capo does not (Bowie Q5). */
  transpose?: number
}) => {
  const vocals = parts.includes('vocals')
  const drums = parts.includes('drums')
  const { tempo, meter, triplet } = song.rhythm ?? {}
  const segments: string[] = []
  const written = song.scale ?? undefined
  const transposed = transpose === 0 || !written ? song : { ...song, scale: concertKeyOf(written, transpose, 'shapes') }
  const key = keySegment(parts, transposed, frame)
  if (key) {
    segments.push(key)
  }
  const tuning = tuningSegment(parts, song)
  if (tuning) {
    segments.push(tuning)
  }
  if (drums) {
    if (tempo) {
      segments.push(tempoLabel(tempo, meter))
    }
    if (meter) {
      segments.push(meterText(meter))
    }
    if ((meter || tempo) && !triplet) {
      segments.push('straight')
    }
  }
  if ((drums || vocals) && meter) {
    segments.push(countInLabel(meter))
  }
  /** Under a pitch frame the chips drop their "(+2)", so the header carries the transpose, once, last */
  if (transpose !== 0) {
    segments.push(transposeText(transpose))
  }
  return segments.length > 0 ? segments.join(' · ') : undefined
}
